/**
 * The clickable filter-card row above the HR Employees directory table. Three
 * at-a-glance tiles — Contractual, Probationers, Anniversary this month — each
 * showing a live count and toggling a server-side list filter when clicked.
 *
 * Mirrors the manager roster's `TeamStatCards`: distinct semantic accents
 * (info / warning / success) so the tiles read at a glance and stay theme-aware
 * (light + dark), a "selected" state expressed as a ring + heavier border
 * rather than a colour swap, and `aria-pressed` so the toggle is announced.
 *
 * Pure display + a toggle callback: the page owns the active-filter state and
 * the stats query. Single-select — clicking a second card switches to it;
 * clicking the active card clears the filter.
 */
import type { EmployeeDirectoryStats } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import type { IconName } from '../primitives/Icon'

/** The three mutually-exclusive directory quick-filters. `null` = no card active. */
export type DirectoryQuickFilter = 'contractual' | 'probationers' | 'anniversary'

interface EmployeeDirectoryCardsProps {
  /** Counts from `getEmployeeDirectoryStats`; undefined while loading. */
  stats: EmployeeDirectoryStats | undefined
  /** Which card (if any) is currently narrowing the list. */
  active: DirectoryQuickFilter | null
  /** Toggle a card. Passing the already-active key clears the filter. */
  onToggle: (key: DirectoryQuickFilter) => void
}

interface Accent {
  bg: string
  fg: string
  line: string
}

const ACCENT_INFO: Accent = {
  bg: 'var(--info-bg)',
  fg: 'var(--info-fg)',
  line: 'var(--info-line)',
}
const ACCENT_WARNING: Accent = {
  bg: 'var(--warning-bg)',
  fg: 'var(--warning-fg)',
  line: 'var(--warning-line)',
}
const ACCENT_SUCCESS: Accent = {
  bg: 'var(--success-bg)',
  fg: 'var(--success-fg)',
  line: 'var(--success-line)',
}

interface CardDef {
  key: DirectoryQuickFilter
  label: string
  icon: IconName
  accent: Accent
  count: (s: EmployeeDirectoryStats) => number
}

const CARDS: ReadonlyArray<CardDef> = [
  {
    key: 'contractual',
    label: 'Contractual',
    icon: 'badge',
    accent: ACCENT_INFO,
    count: (s) => s.contractual,
  },
  {
    key: 'probationers',
    label: 'Probationers',
    icon: 'hourglass_top',
    accent: ACCENT_WARNING,
    count: (s) => s.probationers,
  },
  {
    key: 'anniversary',
    label: 'Anniversary this month',
    icon: 'celebration',
    accent: ACCENT_SUCCESS,
    count: (s) => s.anniversaryThisMonth,
  },
]

function tileStyle(accent: Accent, selected: boolean): React.CSSProperties {
  return {
    flex: '1 1 180px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    border: `1px solid ${selected ? accent.fg : accent.line}`,
    borderRadius: 12,
    background: accent.bg,
    textAlign: 'left',
    boxSizing: 'border-box',
    cursor: 'pointer',
    minHeight: 44,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    // Selected reads as a ring rather than a colour swap, so the accent stays
    // consistent whether the card is on or off.
    boxShadow: selected ? `0 0 0 1px ${accent.fg} inset` : 'none',
  }
}

export function EmployeeDirectoryCards({
  stats,
  active,
  onToggle,
}: EmployeeDirectoryCardsProps): JSX.Element {
  return (
    <div
      data-testid="employee-directory-cards"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}
    >
      {CARDS.map((card) => {
        const selected = active === card.key
        const count = stats ? card.count(stats) : null
        return (
          <button
            key={card.key}
            type="button"
            data-testid={`employee-card-${card.key}`}
            onClick={() => onToggle(card.key)}
            aria-pressed={selected}
            title={
              selected
                ? `Showing only ${card.label.toLowerCase()} — click to clear`
                : `Show only ${card.label.toLowerCase()}`
            }
            style={tileStyle(card.accent, selected)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={card.icon} size={15} color={card.accent.fg} />
              <span style={{ fontSize: 12, fontWeight: 600, color: card.accent.fg }}>
                {card.label}
              </span>
            </span>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
              {count == null ? '—' : count}
            </span>
            <span style={{ fontSize: 11, color: card.accent.fg, opacity: 0.85 }}>
              {selected ? 'Filtering the list · click to clear' : 'Click to filter the list'}
            </span>
          </button>
        )
      })}
    </div>
  )
}
